import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import analyticsService from '../services/analyticsService';
import TradeJournalModal from '../components/TradeJournal/TradeJournalModal';
import { AppStateContext } from './AppStateContext';
import { AuthContext } from './AuthContext';
import { refreshTradeJournalsCache } from '../utils/tradeJournalCache';
import { tradeIdFromPosition } from '../utils/tradeId';
import { useTradeJournals } from '../hooks/useTradeJournals';

const JournalModalContext = createContext();

export const useJournalModal = () => {
  const context = useContext(JournalModalContext);
  if (!context) {
    throw new Error('useJournalModal must be used within a JournalModalProvider');
  }
  return context;
};

export const JournalModalProvider = ({ children }) => {
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [journalToEdit, setJournalToEdit] = useState(null);
  const [positionData, setPositionData] = useState(null);
  const [isAutoOpened, setIsAutoOpened] = useState(false);
  const [pendingTradeId, setPendingTradeId] = useState(null);
  const appStateContext = useContext(AppStateContext);
  const selectedTicker = appStateContext?.selectedTicker || null;
  const { user } = useContext(AuthContext);
  const { getTradeJournalByTradeId } = useTradeJournals();

  const openNewJournal = useCallback((positionData = null, autoOpened = false, tradeId = null) => {
    console.log('[JournalModalContext] openNewJournal called:', { positionData, autoOpened, tradeId });
    
    analyticsService.trackTradingEvent('journal_modal_opened', {
      positionData: positionData,
      autoOpened: autoOpened
    });

    setPositionData(positionData);
    setJournalToEdit(null);
    setIsAutoOpened(autoOpened);
    setPendingTradeId(tradeId || null);
    setShowModal(true);
    
    console.log('[JournalModalContext] Modal state updated: showModal=true');
  }, []);

  const openEditJournal = useCallback((journal) => {
    // Track journal modal opened for editing
    analyticsService.trackTradingEvent('journal_modal_opened', {
      positionData: null,
      autoOpened: false
    });

    setJournalToEdit(journal);
    setPositionData(null);
    setIsAutoOpened(false);
    setPendingTradeId(journal?.trade_id || null);
    setShowModal(true);
  }, []);

  const closeModal = useCallback(() => {
    setShowModal(false);
    setJournalToEdit(null);
    setPositionData(null);
    setIsAutoOpened(false);
    setPendingTradeId(null);
  }, []);

  // Listen for position:deleted events to automatically open journal modal
  useEffect(() => {
    const onPositionDeleted = async (e) => {
      console.log('[JournalModalContext] Received position:deleted event:', e.detail);
      if (e.detail?.position) {
        const tradeId = tradeIdFromPosition(user?.id, e.detail.position);
        console.log('[JournalModalContext] Resolving auto-journal for position:', e.detail.position, 'tradeId:', tradeId);
        if (tradeId) {
          try {
            const existingJournal = await getTradeJournalByTradeId(tradeId);
            if (existingJournal?.id) {
              openEditJournal(existingJournal);
              return;
            }
          } catch (err) {
            console.warn('[JournalModalContext] Failed to resolve journal by trade_id:', err);
          }
        }
        openNewJournal(e.detail.position, true, tradeId);
      } else {
        console.warn('[JournalModalContext] position:deleted event missing position data');
      }
    };

    window.addEventListener('position:deleted', onPositionDeleted);
    return () => {
      window.removeEventListener('position:deleted', onPositionDeleted);
    };
  }, [openEditJournal, openNewJournal, getTradeJournalByTradeId, user?.id]);

  // App-wide: header Journal button, TradeJournalsList row clicks, etc.
  useEffect(() => {
    const onOpen = (e) => {
      if (e.detail?.journal) {
        openEditJournal(e.detail.journal);
      } else {
        openNewJournal(e.detail?.positionData || null, false, e.detail?.tradeId || null);
      }
    };
    window.addEventListener('tradeJournal:open', onOpen);
    return () => window.removeEventListener('tradeJournal:open', onOpen);
  }, [openEditJournal, openNewJournal]);

  // Keep journal lists in sync after modal save (dashboard, trade page, etc.)
  useEffect(() => {
    const onJournalChanged = () => {
      refreshTradeJournalsCache(queryClient);
    };
    window.addEventListener('tradeJournal:created', onJournalChanged);
    window.addEventListener('tradeJournal:updated', onJournalChanged);
    return () => {
      window.removeEventListener('tradeJournal:created', onJournalChanged);
      window.removeEventListener('tradeJournal:updated', onJournalChanged);
    };
  }, [queryClient]);

  const value = {
    showModal,
    journalToEdit,
    positionData,
    isAutoOpened,
    openNewJournal,
    openEditJournal,
    closeModal
  };

  return (
    <JournalModalContext.Provider value={value}>
      {children}
      <TradeJournalModal
        showModal={showModal}
        handleCloseModal={closeModal}
        journalToEdit={journalToEdit}
        positionData={positionData}
        isAutoOpened={isAutoOpened}
        tradeId={pendingTradeId}
      />
    </JournalModalContext.Provider>
  );
};
